import { useEffect, useState } from "react";
import type { CSSProperties, FormEvent } from "react";
import {
  ArrowRight,
  ArrowSquareOut,
  Buildings,
  Check,
  FileArrowDown,
  FileText,
  HardHat,
  Lightning,
  List,
  MagnifyingGlass,
  MapPin,
  SealCheck,
  ShieldCheck,
  Timer,
  X,
} from "@phosphor-icons/react";

const pilotEmail = "hello@munivolt.com";

const stages = [
  {
    number: "01",
    title: "Planning",
    copy: "A fleet plan, facility study, or public meeting shows the need.",
  },
  {
    number: "02",
    title: "Funding",
    copy: "A budget, grant, or utility program makes the project more concrete.",
  },
  {
    number: "03",
    title: "Design",
    copy: "Sites, power needs, delivery paths, and technical details take shape.",
  },
  {
    number: "04",
    title: "Procurement",
    copy: "The formal buying process begins. Preparation time is now shorter.",
  },
];

const briefContents = [
  "The public record that started the signal",
  "The current stage and why we assigned it",
  "Known funding, sites, fleet use, and technical clues",
  "Named vendors, awards, and other dead-end checks",
  "The next public event or record worth watching",
];

const workflow = [
  {
    icon: MagnifyingGlass,
    label: "Monitor",
    title: "Read the records that appear before a bid.",
    copy: "We watch meeting systems, staff reports, budgets, utility agreements, and procurement pages.",
  },
  {
    icon: ShieldCheck,
    label: "Verify",
    title: "Check what happened after the first signal.",
    copy: "We look for newer records, awards, named vendors, cooperative purchases, and changed scope.",
  },
  {
    icon: MapPin,
    label: "Match",
    title: "Send only what fits the contractor.",
    copy: "Territory, project type, self-performed work, and preferred project size shape each delivery.",
  },
];

const faqItems = [
  {
    question: "Is this another bid-alert service?",
    answer:
      "No. Bid alerts usually begin when a solicitation is already public. MuniVolt focuses on earlier planning, funding, utility, and design records. We then check the later procurement status before a brief reaches you.",
  },
  {
    question: "Can my team do this with AI?",
    answer:
      "Yes. AI can summarize a record. Your team would still need to find the right sources, read the attachments, check newer records, remove awarded work, and repeat that process each week. MuniVolt owns that workflow for you.",
  },
  {
    question: "Is the information private or inside government information?",
    answer:
      "No. MuniVolt uses public records. The value comes from finding them early, connecting related records, checking what changed, and explaining the result clearly.",
  },
  {
    question: "Do you guarantee a project or contract award?",
    answer:
      "No. A brief explains public evidence and uncertainty. Your team decides whether the project fits and how to participate. MuniVolt never promises that a procurement will happen or that a contractor will win it.",
  },
  {
    question: "What if the five briefs are not useful?",
    answer:
      "If none is both relevant and new to your team, the founding pilot is refunded. That is the test.",
  },
];

function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [formStatus, setFormStatus] = useState("");

  useEffect(() => {
    const elements = Array.from(document.querySelectorAll<HTMLElement>("[data-reveal]"));

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      elements.forEach((element) => element.classList.add("is-visible"));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: "0px 0px -8%", threshold: 0.12 },
    );

    elements.forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, []);

  function handlePilotRequest(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const name = String(data.get("name") || "").trim();
    const company = String(data.get("company") || "").trim();
    const email = String(data.get("email") || "").trim();
    const territory = String(data.get("territory") || "").trim();
    const note = String(data.get("note") || "").trim();

    if (!name || !company || !email || !territory) {
      setFormStatus("Please complete the four required fields.");
      return;
    }

    const subject = encodeURIComponent(`MuniVolt pilot request from ${company}`);
    const body = encodeURIComponent(
      [
        `Name: ${name}`,
        `Company: ${company}`,
        `Email: ${email}`,
        `California territory: ${territory}`,
        `Notes: ${note || "None provided"}`,
      ].join("\n"),
    );

    setFormStatus("Your email app should open with the request filled in.");
    window.location.href = `mailto:${pilotEmail}?subject=${subject}&body=${body}`;
  }

  return (
    <>
      <a className="skip-link" href="#main-content">Skip to main content</a>

      <div className="utility-bar">
        <div className="shell utility-inner">
          <span>California public-fleet intelligence</span>
          <span className="utility-status"><span className="status-dot" aria-hidden="true" /> Founding contractor pilots</span>
        </div>
      </div>

      <header className="site-header">
        <div className="shell nav-wrap">
          <a className="wordmark" href="#top" aria-label="MuniVolt home">
            <span className="wordmark-mark" aria-hidden="true"><span /><span /></span>
            <span>MuniVolt</span>
          </a>

          <button
            className="menu-button"
            type="button"
            aria-expanded={menuOpen}
            aria-controls="primary-navigation"
            aria-label={menuOpen ? "Close navigation" : "Open navigation"}
            onClick={() => setMenuOpen((value) => !value)}
          >
            {menuOpen ? <X size={22} /> : <List size={22} />}
          </button>

          <nav id="primary-navigation" className={menuOpen ? "primary-nav is-open" : "primary-nav"} aria-label="Primary navigation">
            <a href="#brief" onClick={() => setMenuOpen(false)}>The brief</a>
            <a href="#method" onClick={() => setMenuOpen(false)}>Method</a>
            <a href="#sample" onClick={() => setMenuOpen(false)}>Sample</a>
            <a href="#pilot" onClick={() => setMenuOpen(false)}>Pilot</a>
            <a className="button button-small" href="#request" onClick={() => setMenuOpen(false)}>Request a pilot</a>
          </nav>
        </div>
      </header>

      <main id="main-content">
        <section className="hero" id="top">
          <div className="hero-grid-lines" aria-hidden="true" />
          <div className="shell hero-grid">
            <div className="hero-copy" data-reveal>
              <p className="eyebrow"><span>PRE-RFP</span><span>CALIFORNIA</span></p>
              <h1>See fleet-charging projects before the RFP.</h1>
              <p className="hero-subhead">
                Verified California public records, turned into contractor-ready opportunity briefs. You see what is moving, why it matters, and what to watch next.
              </p>
              <div className="hero-actions">
                <a className="button" href="#request">Request a pilot <ArrowRight size={18} weight="bold" /></a>
                <a className="text-link text-link-light" href="#sample">Open the sample <FileArrowDown size={18} /></a>
              </div>
              <div className="hero-proof flex flex-wrap items-center gap-x-6 gap-y-3">
                <span><HardHat size={18} /> EV charging contractors</span>
                <span><Lightning size={18} /> Electrical contractors</span>
                <span><Buildings size={18} /> Design-build teams</span>
              </div>
            </div>

            <div className="hero-visual" data-reveal>
              <figure className="hero-figure">
                <img
                  src="/images/munivolt-hero.webp"
                  alt="Illustrative municipal fleet charging yard with white work vans under a solar canopy"
                  width="1568"
                  height="1003"
                  fetchPriority="high"
                />
                <figcaption>Illustrative image. No agency or customer endorsement implied.</figcaption>
              </figure>
              <div className="signal-card" aria-label="Example public record signal">
                <div className="signal-card-head">
                  <span className="signal-pulse" aria-hidden="true" />
                  <span>PUBLIC RECORD SIGNAL</span>
                  <span>CA 001</span>
                </div>
                <strong>Santa Rosa Water</strong>
                <p>PG&amp;E EV Fleet Program at the Utilities Field Operations campus</p>
                <div className="signal-meta"><span>Stage 03</span><span>Design + utility</span></div>
              </div>
            </div>
          </div>
        </section>

        <section className="proof-rail" aria-label="MuniVolt verification standards">
          <div className="shell proof-grid">
            <div><SealCheck size={23} weight="duotone" /><span><strong>Source trace</strong> Every material fact links back.</span></div>
            <div><Timer size={23} weight="duotone" /><span><strong>Current stage</strong> Planning is not called a bid.</span></div>
            <div><ShieldCheck size={23} weight="duotone" /><span><strong>Dead-end check</strong> Awards and named vendors are surfaced.</span></div>
          </div>
        </section>

        <section className="section timing-section" id="brief">
          <div className="shell section-heading split-heading" data-reveal>
            <p className="section-label">WHY EARLY MATTERS</p>
            <div>
              <h2>Most lead tools begin at the crowded part.</h2>
              <p>By the time an RFP appears, the agency has already made many choices. Earlier records give your team more time to understand the site, prepare partners, and decide whether the work fits.</p>
            </div>
          </div>

          <div className="shell stage-track" data-reveal>
            {stages.map((stage, index) => (
              <article className={index === stages.length - 1 ? "stage-item stage-item-late" : "stage-item"} key={stage.title}>
                <span className="stage-number">{stage.number}</span>
                <h3>{stage.title}</h3>
                <p>{stage.copy}</p>
              </article>
            ))}
          </div>
          <div className="shell timing-caption" data-reveal>
            <span className="timing-line" aria-hidden="true" />
            <p>MuniVolt works in the preparation window, then keeps checking the project as it moves.</p>
          </div>
        </section>

        <section className="section product-section">
          <div className="shell product-grid">
            <div className="product-visual" data-reveal>
              <img
                src="/images/munivolt-infrastructure.webp"
                alt="Illustrative commercial fleet charger beside electrical cabinets and municipal work vans"
                width="1448"
                height="1086"
                loading="lazy"
              />
              <div className="image-code" aria-hidden="true">FIELD / INFRASTRUCTURE / CA</div>
            </div>
            <div className="product-copy" data-reveal>
              <p className="section-label">THE OPPORTUNITY BRIEF</p>
              <h2>Enough context to make a decision.</h2>
              <p>A link is not the product. The product is a checked explanation of what is happening, why it may matter to your team, and what remains unknown.</p>
              <div className="check-list">
                {briefContents.map((item) => (
                  <div key={item}><Check size={18} weight="bold" aria-hidden="true" /><span>{item}</span></div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="section sample-section" id="sample">
          <div className="shell sample-header" data-reveal>
            <p className="section-label">SAMPLE BRIEF 001</p>
            <h2>See the source trail, not a sales story.</h2>
          </div>

          <div className="shell sample-grid">
            <div className="sample-copy" data-reveal>
              <div className="sample-agency"><span>City of Santa Rosa</span><strong>Water Department</strong></div>
              <p>In 2026, Santa Rosa Water brought a PG&amp;E EV Fleet Program agreement to its Board of Public Utilities for the campus at 35 Stony Point Road.</p>
              <dl className="sample-facts">
                <div><dt>Current read</dt><dd>Utility coordination and design-stage activity</dd></div>
                <div><dt>Contractor angle</dt><dd>Possible future city-side EVSE and behind-the-meter work</dd></div>
                <div><dt>Important limit</dt><dd>No later city-side equipment or construction award was located in the sources reviewed.</dd></div>
              </dl>
              <div className="source-links">
                <a href="https://santa-rosa.legistar.com/View.ashx?GUID=28528E69-81BF-4362-9682-B51A12BD1684&ID=15481958&M=F" target="_blank" rel="noreferrer">Official staff report <ArrowSquareOut size={16} /></a>
                <a href="https://santa-rosa.legistar.com/MeetingDetail.aspx?GUID=FDE6C67C-4ED4-4E8B-BB47-89DC11DA1383&ID=1364314&Options=&Search=" target="_blank" rel="noreferrer">Official meeting record <ArrowSquareOut size={16} /></a>
              </div>
              <a className="button button-dark" href="/MuniVolt-Sample-Opportunity-Brief.pdf" target="_blank" rel="noreferrer">View the full brief <FileText size={18} /></a>
            </div>

            <a className="brief-preview" href="/MuniVolt-Sample-Opportunity-Brief.pdf" target="_blank" rel="noreferrer" aria-label="Open the MuniVolt sample Opportunity Brief PDF" data-reveal>
              <div className="preview-topline"><span>MUNIVOLT / OPPORTUNITY BRIEF</span><span>001</span></div>
              <img src="/images/sample-brief-page-1.png" alt="First page of the MuniVolt Santa Rosa sample Opportunity Brief" width="1275" height="1650" loading="lazy" />
            </a>
          </div>
        </section>

        <section className="section method-section" id="method">
          <div className="shell section-heading split-heading" data-reveal>
            <p className="section-label">THE METHOD</p>
            <div>
              <h2>AI makes the search faster. A person signs off on the brief.</h2>
              <p>The hard part is not summarizing one document. It is finding the right records, connecting them, checking what happened later, and saying when the evidence is incomplete.</p>
            </div>
          </div>

          <div className="shell workflow-grid">
            {workflow.map(({ icon: Icon, label, title, copy }, index) => (
              <article data-reveal key={label} style={{ "--reveal-delay": `${index * 90}ms` } as CSSProperties}>
                <div className="workflow-index"><Icon size={28} weight="duotone" /><span>{String(index + 1).padStart(2, "0")}</span></div>
                <p className="section-label">{label}</p>
                <h3>{title}</h3>
                <p>{copy}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="section pilot-section" id="pilot">
          <div className="shell pilot-grid">
            <div className="pilot-copy" data-reveal>
              <p className="section-label">FOUNDING CONTRACTOR PILOT</p>
              <h2>Test the intelligence before you subscribe.</h2>
              <p>The first week answers one useful question: does earlier, verified information change what your team pays attention to?</p>
              <p className="pilot-fit">Best fit: California EV charging contractors, commercial electrical contractors, and design-build teams that want public-fleet work.</p>
            </div>
            <div className="pilot-offer" data-reveal>
              <div className="price-row">
                <div><span>Seven days</span><small>No long-term contract</small></div>
                <strong>$249</strong>
              </div>
              <ul>
                <li><Check size={17} weight="bold" /> Five current briefs matched to your operation</li>
                <li><Check size={17} weight="bold" /> New qualifying signals found during the week</li>
                <li><Check size={17} weight="bold" /> A short territory and capability review</li>
                <li><Check size={17} weight="bold" /> Sources, unknowns, and disqualifiers included</li>
              </ul>
              <div className="refund-note"><SealCheck size={23} weight="duotone" /><p>If none is both relevant and new to your team, the pilot is refunded.</p></div>
              <a className="button" href="#request">Request a pilot <ArrowRight size={18} weight="bold" /></a>
            </div>
          </div>
        </section>

        <section className="section faq-section">
          <div className="shell faq-grid">
            <div data-reveal><p className="section-label">PLAIN ANSWERS</p><h2>Know what you are buying.</h2></div>
            <div className="faq-list" data-reveal>
              {faqItems.map((item) => (
                <details key={item.question}><summary>{item.question}</summary><p>{item.answer}</p></details>
              ))}
            </div>
          </div>
        </section>

        <section className="section request-section" id="request">
          <div className="shell request-grid">
            <div data-reveal>
              <p className="section-label">START WITH FIT</p>
              <h2>Tell us what your team pursues.</h2>
              <p>Share your California territory and the work you want. We will confirm whether the pilot fits before taking payment.</p>
              <p className="direct-email">Prefer email? <a href={`mailto:${pilotEmail}`}>{pilotEmail}</a></p>
            </div>
            <form className="pilot-form" onSubmit={handlePilotRequest} noValidate data-reveal>
              <div className="form-row">
                <label>Your name<input name="name" autoComplete="name" required /></label>
                <label>Company<input name="company" autoComplete="organization" required /></label>
              </div>
              <label>Work email<input name="email" type="email" autoComplete="email" required /></label>
              <label>
                California territory
                <input name="territory" placeholder="Example: Bay Area and Sacramento" aria-describedby="territory-help" required />
                <span id="territory-help" className="helper-text">Counties, utility areas, or regions are all useful.</span>
              </label>
              <label>What work do you want?<textarea name="note" rows={4} placeholder="Example: fleet-yard electrical work, DC fast charging, or design-build projects" /></label>
              <button className="button" type="submit">Request a pilot <ArrowRight size={18} weight="bold" /></button>
              <p className="form-status" role="status" aria-live="polite">{formStatus}</p>
            </form>
          </div>
        </section>
      </main>

      <footer className="site-footer">
        <div className="shell footer-grid">
          <div>
            <a className="wordmark footer-wordmark" href="#top"><span className="wordmark-mark" aria-hidden="true"><span /><span /></span><span>MuniVolt</span></a>
            <p>Early intelligence for public-fleet charging contractors.</p>
          </div>
          <div className="footer-note">
            <p>MuniVolt summarizes public records. We do not represent government agencies, sell inside information, or guarantee that a procurement will occur.</p>
            <span>© 2026 MuniVolt</span>
          </div>
        </div>
      </footer>
    </>
  );
}

export default App;
